import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Modal, Radio, Upload } from 'antd'
import { CameraOutlined, UploadOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { useUploadAvatarMutation } from '@/hooks/useProfile'
import { useQueryClient } from '@tanstack/react-query'
import { invalidateAdvisoryPopups, useUserMedia } from '@/features/advisory-ai/hooks'

const VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
const MAX_SIZE = 5 * 1024 * 1024

export function AvatarSettingsPanel() {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const uploadAvatarMutation = useUploadAvatarMutation()
  const { data: media } = useUserMedia()
  const avatarInfo = media?.avatar
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isMale, setIsMale] = useState(true)
  const [consent, setConsent] = useState(false)

  // Camera capture: front-facing by default, mirrored preview, captured frame reuses the upload flow.
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user')
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const cameraStreamRef = useRef<MediaStream | null>(null)

  const stopCamera = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop())
    cameraStreamRef.current = null
    setCameraReady(false)
  }, [])

  const startCamera = useCallback(
    async (mode: 'user' | 'environment') => {
      if (!navigator.mediaDevices?.getUserMedia) {
        addToast('Camera is not supported in this browser', 'error')
        setCameraOpen(false)
        return
      }
      stopCamera()
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode } })
        cameraStreamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play().catch(() => {})
        }
        setCameraReady(true)
      } catch {
        addToast('Camera access was denied or unavailable', 'error')
        setCameraOpen(false)
      }
    },
    [addToast, stopCamera]
  )

  // Open/close the camera stream alongside the modal, and tear it down on unmount.
  useEffect(() => {
    if (cameraOpen) void startCamera(facingMode)
    else stopCamera()
    return () => stopCamera()
  }, [cameraOpen, facingMode, startCamera, stopCamera])

  const capturePhoto = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) {
      addToast('Camera is still starting — try again in a moment', 'warning')
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      addToast('Could not capture the photo', 'error')
      return
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          addToast('Could not capture the photo', 'error')
          return
        }
        const file = new File([blob], `avatar-${Date.now()}.png`, { type: 'image/png' })
        handleFileChange(file)
        setCameraOpen(false)
      },
      'image/png'
    )
  }

  const handleFileChange = (file: File | null) => {
    if (file) {
      if (!VALID_TYPES.includes(file.type)) {
        addToast('Invalid file type. Allowed: jpg, jpeg, png, gif, webp', 'error')
        return
      }
      if (file.size > MAX_SIZE) {
        addToast('File size exceeds 5MB limit', 'error')
        return
      }
      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setAvatarPreview(reader.result as string)
      reader.readAsDataURL(file)
    } else {
      setAvatarFile(null)
      setAvatarPreview(null)
    }
  }

  const handleUpload = async () => {
    if (!avatarFile) {
      addToast('Please select or capture a photo first', 'warning')
      return
    }
    if (!consent) {
      addToast('Please confirm consent before uploading your avatar', 'warning')
      return
    }
    try {
      await uploadAvatarMutation.mutateAsync({ file: avatarFile, isMale })
      // Fires `avatar_setup` server-side. Invalidated here rather than inside
      // the shared upload hook, which is generic and should not know about
      // AdvicePrep pop-ups.
      invalidateAdvisoryPopups(queryClient)
      addToast('Avatar uploaded successfully', 'success')
      setAvatarFile(null)
      setAvatarPreview(null)
      setIsMale(true)
      setConsent(false)
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to upload avatar', 'error')
    }
  }

  return (
    <div className="space-y-6 border border-gray-200 rounded-lg bg-white p-6">
      <div className="flex justify-center">
        <div className="w-64 h-64 border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
          {avatarPreview || avatarInfo?.url ? (
            <img
              src={avatarPreview ?? avatarInfo?.url ?? ''}
              alt="Avatar preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-gray-400 text-center p-4">
              <CameraOutlined className="text-4xl mb-2" />
              <p className="text-sm">No image selected</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <Upload
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          beforeUpload={(file) => {
            handleFileChange(file)
            return false
          }}
          showUploadList={false}
        >
          <Button className="feature-button-outline" icon={<UploadOutlined />}>
            Choose File
          </Button>
        </Upload>
        <Button icon={<CameraOutlined />} onClick={() => setCameraOpen(true)}>
          Take Photo
        </Button>
      </div>

      <div className="space-y-2 text-sm text-gray-600">
        <p>Allowed file types: jpg, jpeg, png, gif, webp</p>
        <p>Maximum file size: 5MB</p>
        <p>Requirements: Clean Background and Lighting, Transparent Backgroud, Face should be clearly visible, Formal / Semi-Formal Attire</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
        <Radio.Group value={isMale} onChange={(e) => setIsMale(e.target.value)}>
          <Radio value={true}>Male</Radio>
          <Radio value={false}>Female</Radio>
        </Radio.Group>
      </div>

      <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)}>
        I confirm this is my own image and I consent to it being used to generate my AdvicePrep videos.
      </Checkbox>

      <div className="flex justify-center">
        <Button
          type="primary"
          className="feature-button-solid"
          icon={<UploadOutlined />}
          onClick={handleUpload}
          loading={uploadAvatarMutation.isPending}
          disabled={!avatarFile || !consent}
        >
          Upload Avatar
        </Button>
      </div>

      <Modal
        title="Take a Photo"
        open={cameraOpen}
        onCancel={() => setCameraOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div className="space-y-4">
          <div className="relative w-full aspect-square overflow-hidden rounded-lg bg-gray-900 flex items-center justify-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : undefined }}
            />
            {!cameraReady ? (
              <span className="absolute text-sm text-gray-300">Starting camera…</span>
            ) : (
              <div className="pointer-events-none absolute inset-8 rounded-full border-2 border-dashed border-white/40" />
            )}
          </div>
          <p className="text-xs text-gray-500 text-center">
            Center your face in the circle. Use even lighting and a plain background.
          </p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => setFacingMode((mode) => (mode === 'user' ? 'environment' : 'user'))}>
              Switch Camera
            </Button>
            <Button
              type="primary"
              className="feature-button-solid"
              icon={<CameraOutlined />}
              disabled={!cameraReady}
              onClick={capturePhoto}
            >
              Capture
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
